import { useLang } from 'hooks/lang';
import { TFunction } from 'i18next';

import { actions, loaders, Project, projectStatusConfig } from 'entities';

import { Skeleton, Column, Row, Button, Dot } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { OwnerSelect } from 'components/Inputs';

import {
  UserIcon,
  TicketIcon,
  LocationPinIcon,
  ClockIcon,
  UserPlusIcon,
  LocationMapIcon,
} from 'components/Icons';

import { useProject } from 'providers/Project';
import { useMultiStateV2, useUser } from 'hooks';
import { usePendingActions } from 'providers/PendingActions';

import { ActiveProjectsByUser } from './ActiveProjectsByUser';
import { ContentCard, ProjectLink, TwoColumnWrapper } from '../components';

import { listRemove } from 'utils/array';
import { cascadeEval } from 'utils/conditions';
import { getCountryLabel } from 'utils/countries';

const projectProps: Array<{
  getName: (t: TFunction) => string;
  extractor: (project: Project) => string;
  Icon: typeof UserIcon;
}> = [
  {
    getName: (t: TFunction) => t('client'),
    extractor: ({ clientName }) => clientName,
    Icon: UserIcon,
  },
  {
    getName: (t: TFunction) => t('client-origin'),
    extractor: ({ clientOrigin }) => clientOrigin,
    Icon: LocationMapIcon,
  },
  {
    getName: (t: TFunction) => t('freshdesk-ticket'),
    extractor: ({ ticket }) => ticket,
    Icon: TicketIcon,
  },
  {
    getName: (t: TFunction) => t('countries'),
    extractor: ({ countries }) => countries.map((code) => getCountryLabel(code)).join(', '),
    Icon: LocationPinIcon,
  },
  {
    getName: (t: TFunction) => t('estimated-working-days'),
    extractor: ({ estimatedCompletionWorkingDays }) => `${estimatedCompletionWorkingDays}`,
    Icon: ClockIcon,
  },
];

type State = Record<string, string | boolean>;

const loadingKey = (project: string | { id: string }): string =>
  `loading_${typeof project === 'string' ? project : project.id}`;

export function ProjectAssign() {
  const { t } = useLang();
  const user = useUser();
  const { decreaseCount } = usePendingActions();

  const [state, { dispatch }] = useMultiStateV2({} as State);

  const { projects: appProjects, updateProject } = useProject();

  const unassignProjects = loaders.projects.unassignedProjects.use({
    defaultValue: [],
  });

  // this is not done onParse above as real time updates happen across the app
  const actualProjects = unassignProjects.data.filter(({ id, teamResponsible }) =>
    appProjects[id]
      ? appProjects[id].status === projectStatusConfig.unassigned.code &&
        teamResponsible === user.team.id
      : true,
  );

  const assignProject = actions.projects.assignProject.use({
    onSuccess: (assigned) => {
      unassignProjects.overwrite((old) => listRemove(old, assigned.id));

      updateProject(assigned.id, (old) => ({ ...old, ...assigned }));

      decreaseCount('UNASSIGNED_PROJECTS');
    },
  });

  const handleSaveClick = async (id: string) => {
    const owner = state[id] as string;

    if (!owner) return;

    dispatch({ [loadingKey(id)]: true });

    await assignProject.execute({
      id,
      owner,
    });

    dispatch({ [loadingKey(id)]: false });
  };

  return (
    <TwoColumnWrapper>
      {cascadeEval([
        {
          is: unassignProjects.loading,
          then: () => <Skeleton quantity={2} height={290} />,
        },
        {
          is: !actualProjects.length,
          then: () => <p className="text-center">{t('no-unassigned-projects-found')}</p>,
        },
        {
          is: true,
          then: () => (
            <Column>
              {actualProjects.map((project) => (
                <ContentCard
                  key={project.id}
                  header={
                    <>
                      <Dot color={projectStatusConfig.unassigned.color} size={16} />

                      <ProjectLink id={project.id} text={project.name} />
                    </>
                  }
                  content={
                    <Row
                      alignItems="flex-start"
                      justifyContent="space-between"
                      fullWidth
                      className="small_tablet:flex-col"
                    >
                      <Column gap="6">
                        {projectProps.map(({ getName, extractor, Icon }) => (
                          <PropertyWithIcon
                            key={getName(t)}
                            icon={<Icon />}
                            name={getName(t)}
                            value={extractor(project)}
                          />
                        ))}
                      </Column>

                      <Column className="small_tablet:mt-2 small_tablet:w-full small_tablet:items-center">
                        <OwnerSelect
                          value={(state[project.id] as string) || ''}
                          onChange={(value) => dispatch({ [project.id]: value })}
                        />

                        {!!state[project.id] && (
                          <Button
                            variant="outlined"
                            onClick={() => handleSaveClick(project.id)}
                            loading={!!state[loadingKey(project)]}
                          >
                            <UserPlusIcon size={20} />
                            {t('save')}
                          </Button>
                        )}
                      </Column>
                    </Row>
                  }
                ></ContentCard>
              ))}
            </Column>
          ),
        },
      ])}

      <ActiveProjectsByUser />
    </TwoColumnWrapper>
  );
}
